import { toast } from '@livable/saas-react-components';
import { useEffect } from 'react';
import { api } from 'src/api';
import { downloadErrorMessage, triggerDownload } from './billsDownload';
const NO_OPTIONS = {
  cancelToken: {}
};

/**
 * The link in a bills download email (LB-6033): `/bills?export={id}`. It asks for the download again rather than
 * carrying a link of its own, because a link signed for the file stops working long before the file does. Acted on
 * once, then `onHandled` takes it out of the address so a refresh does not download it again.
 */
export function useBillsExportLink(_ref) {
  let companyId = _ref.companyId,
    exportId = _ref.exportId,
    onHandled = _ref.onHandled;
  useEffect(() => {
    if (!exportId || !companyId) return undefined;
    let isActive = true;
    const open = async () => {
      try {
        var _await$api$BillAutoma;
        const result = (_await$api$BillAutoma = await api.BillAutomationBills.getBillsExport({
          companyId,
          exportId
        }, NO_OPTIONS)) === null || _await$api$BillAutoma === void 0 ? void 0 : _await$api$BillAutoma.response;
        if (!isActive) return;
        if ((result === null || result === void 0 ? void 0 : result.status) === 'ready' && result.url) {
          triggerDownload(result.url);
          toast('success', {
            body: ["".concat(result.fileName, " is downloading.")]
          });
        } else if ((result === null || result === void 0 ? void 0 : result.status) === 'failed') {
          toast('error', {
            title: "This download couldn't be prepared",
            body: [result.error || 'Download it again from this page.']
          });
        } else if ((result === null || result === void 0 ? void 0 : result.status) === 'building') {
          toast('info', {
            body: ["Your zip is still being prepared. We'll email you when it's ready."]
          });
        } else {
          toast('warning', {
            title: "This download isn't available",
            body: ['Download the bills again from this page.']
          });
        }
      } catch (err) {
        var _err$response;
        if (!isActive) return;
        const status = err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 ? void 0 : _err$response.status;
        toast('warning', {
          title: status === 410 ? 'This download has expired' : "This download isn't available",
          body: [status === 410 ? 'Downloads are kept for 7 days. Download it again from this page.' : status === 404 ? 'It belongs to someone else, or no longer exists. Download the bills again from this page.' : downloadErrorMessage(err)]
        });
      } finally {
        if (isActive) onHandled();
      }
    };
    open();
    return () => {
      isActive = false;
    };
    // Once per link: onHandled is a new function on every render, and running again for it would download twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, exportId]);
}